import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { buildApiUrl } from "../utils/apiConfig";
import { formatUsDate, parseCompleteByInput, toDueAtPayload, todayIsoDateInUsEastern } from "../utils/completeByDate";
import "../styles/add-task-modal.css";
import { apiClient } from "../utils/apiClient";
import { mutationErrorFromBody } from "../utils/apiErrorMessage";

type Props = {
    open: boolean;
    onClose: () => void;
    onTaskCreated?: () => void;
    preselectedAthleteEmail?: string; // When opened from GamePlanPage
    isAthleteCreating?: boolean; // When an athlete user is creating a task for themselves
};

type Athlete = {
    email: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
};

type TaskData = {
    name: string;
    description: string;
    file?: File | null;
    sendEmail: boolean;
    completeBy: string;
};

export default function AddTaskModal({ open, onClose, onTaskCreated, preselectedAthleteEmail, isAthleteCreating = false }: Props) {
    const [taskData, setTaskData] = useState<TaskData>({
        name: "",
        description: "",
        file: null,
        sendEmail: true,
        completeBy: ""
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>("");

    // Athlete selection state
    const [selectedAthletes, setSelectedAthletes] = useState<Athlete[]>([]);
    const [allAthletesSelected, setAllAthletesSelected] = useState(false);
    const [athleteInput, setAthleteInput] = useState("");
    const [athleteSuggestions, setSuggestions] = useState<Athlete[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [searchingAthletes, setSearchingAthletes] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close modal on Escape key
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => (open && e.key === "Escape" ? onClose() : undefined);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // Fetch preselected athlete when modal opens
    useEffect(() => {
        if (!open) return;

        // Determine if we need to fetch athlete data
        const shouldFetchAthlete = isAthleteCreating || (preselectedAthleteEmail && preselectedAthleteEmail.trim().length > 0);

        if (!shouldFetchAthlete) {
            setSelectedAthletes([]);
            return;
        }

        const fetchPreselectedAthlete = async () => {
            try {
                let apiUrl: string;
                let fallbackEmail = '';

                // If athlete is creating task for themselves, use /api/athletes/me
                if (isAthleteCreating) {
                    apiUrl = buildApiUrl('api/athletes/me');
                } else if (preselectedAthleteEmail) {
                    // If admin/staff is creating task for a specific athlete
                    apiUrl = buildApiUrl(`api/athletes/${encodeURIComponent(preselectedAthleteEmail)}`);
                    fallbackEmail = preselectedAthleteEmail;
                } else {
                    setSelectedAthletes([]);
                    return;
                }

                const response = await apiClient(apiUrl, {
                    headers: { Accept: "application/json" },
                    credentials: "include"
                });

                if (response.ok) {
                    const athlete = await response.json();
                    setSelectedAthletes([{
                        email: athlete.email || athlete.username || fallbackEmail,
                        firstName: athlete.firstName || "",
                        lastName: athlete.lastName || "",
                        imageUrl: athlete.imageUrl
                    }]);
                } else {
                    // If fetch fails, still show the email as a fallback
                    if (fallbackEmail) {
                        setSelectedAthletes([{
                            email: fallbackEmail,
                            firstName: fallbackEmail.split('@')[0] || "",
                            lastName: "",
                            imageUrl: undefined
                        }]);
                    } else {
                        setSelectedAthletes([]);
                    }
                }
            } catch (error) {
                // If fetch fails, still show the email as a fallback
                const fallbackEmail = preselectedAthleteEmail || '';
                if (fallbackEmail) {
                    setSelectedAthletes([{
                        email: fallbackEmail,
                        firstName: fallbackEmail.split('@')[0] || "",
                        lastName: "",
                        imageUrl: undefined
                    }]);
                } else {
                    setSelectedAthletes([]);
                }
            }
        };

        fetchPreselectedAthlete();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, preselectedAthleteEmail, isAthleteCreating]);

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setTaskData({
                name: "",
                description: "",
                file: null,
                sendEmail: true,
                completeBy: ""
            });
            setSelectedAthletes([]);
            setAllAthletesSelected(false);
            setAthleteInput("");
            setSuggestions([]);
            setShowSuggestions(false);
            setError("");
        }
    }, [open]);

    // Search athletes as user types
    useEffect(() => {
        const searchAthletes = async () => {
            if (!athleteInput.trim() || allAthletesSelected) {
                setSuggestions([]);
                return;
            }

            // Check if user typed "all" (case insensitive)
            if (athleteInput.toLowerCase() === "all") {
                setAllAthletesSelected(true);
                setSelectedAthletes([]);
                setAthleteInput("");
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            setSearchingAthletes(true);

            try {
                const response = await apiClient(
                    buildApiUrl(`api/athletes/search?query=${encodeURIComponent(athleteInput.trim())}&size=200`),
                    {
                        headers: { Accept: "application/json" },
                        credentials: "include" // Use HTTP-only cookie for auth
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    const athletes = Array.isArray(data.content) ? data.content : [];

                    // Filter out already selected athletes
                    const filtered = athletes.filter(
                        (a: any) => !selectedAthletes.some(sel => sel.email === a.email)
                    );

                    setSuggestions(filtered.map((a: any) => ({
                        email: a.email || a.id,
                        firstName: a.firstName || "",
                        lastName: a.lastName || "",
                        imageUrl: a.profilePictureUrl || a.imageUrl
                    })));
                    setShowSuggestions(true);
                } else {
                    setSuggestions([]);
                }
            } catch (error) {
                console.error("Failed to search athletes:", error);
                setSuggestions([]);
            } finally {
                setSearchingAthletes(false);
            }
        };

        const timer = setTimeout(searchAthletes, 300);
        return () => clearTimeout(timer);
    }, [athleteInput, selectedAthletes, allAthletesSelected]);

    const handleSelectAthlete = (athlete: Athlete) => {
        setSelectedAthletes(prev => [...prev, athlete]);
        setAthleteInput("");
        setSuggestions([]);
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const handleRemoveAthlete = (email: string) => {
        setSelectedAthletes(prev => prev.filter(a => a.email !== email));
    };

    const handleRemoveAll = () => {
        setAllAthletesSelected(false);
    };

    const handleCreateTask = async () => {
        // Validation
        if (!taskData.name.trim() || taskData.name.trim().length < 2) {
            setError("Task name must be at least 2 characters");
            return;
        }

        if (!taskData.description.trim() || taskData.description.trim().length < 2) {
            setError("Description must be at least 2 characters");
            return;
        }

        if (!allAthletesSelected && selectedAthletes.length === 0) {
            setError("Please select at least one athlete or 'All'");
            return;
        }

        const completeBy = parseCompleteByInput(taskData.completeBy);
        if ("error" in completeBy) {
            setError(completeBy.error);
            return;
        }

        setSaving(true);
        setError("");

        try {
            const payload = {
                name: taskData.name.trim(),
                description: taskData.description.trim(),
                sendEmail: taskData.sendEmail,
                allAthletes: allAthletesSelected,
                athletes: allAthletesSelected ? null : selectedAthletes.map(a => a.email),
                dueAt: toDueAtPayload(completeBy.isoDate),
                allDay: true
            };

            // Note: File handling removed. If files need to be uploaded,
            // they should be uploaded separately first, and the file reference
            // should be added to the payload

            const response = await apiClient(buildApiUrl('api/tasks'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include', // Use HTTP-only cookie for auth
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({} as { detail?: string; message?: string }));
                throw new Error(mutationErrorFromBody(
                    response.status,
                    errorData,
                    "Unable to create this task. Please try again.",
                    "Unable to create this task. Please try again.",
                ));
            }

            // Success
            onClose();
            onTaskCreated?.();

        } catch (error: any) {
            setError(error?.message || "Failed to create task. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <div className="task-modal-backdrop">
            <div className="task-modal">
                <div className="task-modal-header">
                    <h2>Create new task</h2>
                    <button className="task-modal-close" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className="task-modal-content">
                    {error && (
                        <div className="task-error-message">
                            {error}
                        </div>
                    )}

                    <div className="task-assignee-section">
                        <span className="task-label">For</span>

                        <div className="task-assignee-input-wrapper">
                            {/* Container that looks like an input field but contains tags + input */}
                            <div className={`task-assignee-input-container ${isAthleteCreating ? 'disabled' : ''}`}>
                                {/* Show "All" tag if selected */}
                                {allAthletesSelected ? (
                                    <div className="task-assignee-tag all-tag">
                                        <div className="assignee-avatar fallback-avatar">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                <circle cx="9" cy="7" r="4"/>
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                            </svg>
                                        </div>
                                        <span className="assignee-name">All</span>
                                        {!isAthleteCreating && (
                                            <button
                                                className="assignee-remove"
                                                onClick={handleRemoveAll}
                                                aria-label="Remove All"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        {/* Show selected athlete tags */}
                                        {selectedAthletes.map(athlete => (
                                            <div key={athlete.email} className="task-assignee-tag">
                                                {athlete.imageUrl ? (
                                                    <img
                                                        src={athlete.imageUrl}
                                                        alt={`${athlete.firstName} ${athlete.lastName}`}
                                                        className="assignee-avatar"
                                                    />
                                                ) : (
                                                    <div className="assignee-avatar fallback-avatar">
                                                        {athlete.firstName.charAt(0)}{athlete.lastName.charAt(0)}
                                                    </div>
                                                )}
                                                <span className="assignee-name">
                                                    {athlete.firstName} {athlete.lastName}
                                                </span>
                                                {!isAthleteCreating && (
                                                    <button
                                                        className="assignee-remove"
                                                        onClick={() => handleRemoveAthlete(athlete.email)}
                                                        aria-label={`Remove ${athlete.firstName} ${athlete.lastName}`}
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ))}

                                        {/* Input field for adding more athletes - hide when athlete is creating for themselves */}
                                        {!isAthleteCreating && (
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                className="athlete-input-inline"
                                                placeholder={selectedAthletes.length === 0 ? "Type athlete name or 'all'..." : ""}
                                                value={athleteInput}
                                                onChange={(e) => setAthleteInput(e.target.value)}
                                                onFocus={() => setShowSuggestions(true)}
                                            />
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Suggestions dropdown - only show when not athlete creating */}
                            {!isAthleteCreating && !allAthletesSelected && showSuggestions && athleteInput && (
                                <div className="athlete-suggestions">
                                    {searchingAthletes ? (
                                        <div className="suggestion-item loading">Searching...</div>
                                    ) : athleteSuggestions.length > 0 ? (
                                        athleteSuggestions.map(athlete => (
                                            <div
                                                key={athlete.email}
                                                className="suggestion-item"
                                                onClick={() => handleSelectAthlete(athlete)}
                                            >
                                                {athlete.imageUrl ? (
                                                    <img
                                                        src={athlete.imageUrl}
                                                        alt={`${athlete.firstName} ${athlete.lastName}`}
                                                        className="suggestion-avatar"
                                                    />
                                                ) : (
                                                    <div className="suggestion-avatar fallback-avatar">
                                                        {athlete.firstName.charAt(0)}{athlete.lastName.charAt(0)}
                                                    </div>
                                                )}
                                                <span>{athlete.firstName} {athlete.lastName}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="suggestion-item no-results">No athletes found</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="task-form">
                        <input
                            type="text"
                            placeholder="Task name"
                            className="task-name-input"
                            value={taskData.name}
                            onChange={(e) => setTaskData(prev => ({ ...prev, name: e.target.value }))}
                            required
                        />

                        <textarea
                            placeholder="Description..."
                            className="task-description-input"
                            rows={8}
                            value={taskData.description}
                            onChange={(e) => setTaskData(prev => ({ ...prev, description: e.target.value }))}
                            required
                        />

                        <label className="task-complete-by">
                            <span className="task-label">Complete by:</span>
                            <div className="complete-by-picker">
                                <input
                                    type="text"
                                    className="task-complete-by-input"
                                    value={taskData.completeBy ? formatUsDate(taskData.completeBy) : ""}
                                    placeholder="MM/DD/YYYY"
                                    readOnly
                                    aria-label="Complete by"
                                    data-testid="task-complete-by"
                                    onClick={() => {
                                        const picker = document.getElementById("add-task-date-picker") as HTMLInputElement;
                                        picker?.showPicker?.();
                                        picker?.focus();
                                    }}
                                />
                                <input
                                    id="add-task-date-picker"
                                    type="date"
                                    className="complete-by-native-picker"
                                    value={taskData.completeBy}
                                    min={todayIsoDateInUsEastern()}
                                    onChange={(e) => setTaskData(prev => ({ ...prev, completeBy: e.target.value }))}
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    data-testid="task-complete-by-native"
                                />
                                <button
                                    type="button"
                                    className="complete-by-calendar-btn"
                                    aria-label="Pick date"
                                    onClick={() => {
                                        const picker = document.getElementById("add-task-date-picker") as HTMLInputElement;
                                        picker?.showPicker?.();
                                        picker?.focus();
                                    }}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                </button>
                            </div>
                        </label>

                        <div className="task-options">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={taskData.sendEmail}
                                    onChange={(e) => setTaskData(prev => ({ ...prev, sendEmail: e.target.checked }))}
                                />
                                <span className="checkbox-custom"></span>
                                Send this task as an email
                            </label>

                            <button
                                className="create-task-btn"
                                onClick={handleCreateTask}
                                disabled={saving}
                            >
                                {saving ? 'Creating...' : 'Create Task'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
